import { inject, Injectable } from '@angular/core';
import { Storage as StorageService } from '@core/services/storage.svc';
import { Account, PortalAccountRole } from '@legacy-models/portal/Account';
import { Contact, PortalRoles } from '@legacy-models/portal/Contact';

interface ContactModule {
  Name: string;
}

@Injectable({
  providedIn: 'root'
})
export class Permissions {
  currentUser: Contact | null = null;
  currentAccount: Account | null = null;
  isONEOKAdmin: boolean;
  private readonly _storageSvc = inject(StorageService);

  constructor() {
    this._storageSvc.currentAccount$.subscribe(currentAcc => {
      if (currentAcc) {
        this.currentAccount = currentAcc;
      }
    });
  }

  isMisconfiguration(): boolean {
    this.refreshCurrentContact();
    if (!this.currentUser) {
      return false;
    }
    if (this.hasPortalAdminRole() || this.hasPortalEmployeeRole()) {
      return false;
    }
    const nggpRoles = ['check detail', 'gas settlement', 'invoice', 'measured volumes', 'outage'];
    const roles: string[] = [];
    const businessSegments = (this.currentUser.BusinessSegments as string[] | undefined)?.map(segment =>
      segment.toLocaleLowerCase()
    );
    this.currentUser.PortalRoles?.forEach((portalRole: PortalRoles) => {
      if (portalRole.RoleName !== 'Account Access') {
        roles.push(portalRole.RoleName.replace(' User', '').toLocaleLowerCase());
      }
    });
    // Has 'BusinessSegment' of 'NGP Intrastate' and NO NGP-related Portal Roles
    let isMisconfiguration =
      (businessSegments?.some(segment => segment.includes('ngp')) ?? false) &&
      !roles.some(role => role.includes('ngp'));
    // If Contact has 'BusinessSegment' of 'NGGP' and NO NGGP-related Portal Roles
    if (!isMisconfiguration) {
      isMisconfiguration =
        (businessSegments?.some(segment => segment === 'nggp') ?? false) &&
        !roles.some(role => nggpRoles.includes(role));
    }
    return isMisconfiguration;
  }

  hasExternalAdminRole(): boolean {
    if (!this.hasAccountRole('Admin')) {
      return false;
    }
    this.refreshCurrentContact();
    let hasRole = false;
    if (this.hasPortalAdminRole()) {
      hasRole = true;
    } else {
      this.currentUser?.PortalRoles?.forEach((portalRole: PortalRoles) => {
        if (portalRole.AccountId === this.currentAccount?.AccountId) {
          if (portalRole.RoleName === 'Admin') {
            hasRole = true;
          }
        }
      });
    }

    return hasRole;
  }

  //Modifications for getting Contacts Role's started - Travis
  hasGasSettlementRole(): boolean {
    this.refreshCurrentContact();
    return !!(this.currentUser?.Modules as ContactModule[] | undefined)?.find(element => element.Name === 'Settlement');
  }

  hasCheckDetailsRole(): boolean {
    this.refreshCurrentContact();
    return !!(this.currentUser?.Modules as ContactModule[] | undefined)?.find(
      element => element.Name === 'Check Details'
    );
  }

  hasMeasuredVolumesRole(): boolean {
    this.refreshCurrentContact();
    return !!(this.currentUser?.Modules as ContactModule[] | undefined)?.find(
      element => element.Name === 'Measured Volumes'
    );
  }

  hasNGPUserRole(): boolean {
    this.refreshCurrentContact();
    return !!(this.currentUser?.Modules as ContactModule[] | undefined)?.find(element => element.Name === 'NGP User');
  }

  hasPortalAdminRole(): boolean {
    this.refreshCurrentContact();
    let hasRole = false;
    this.currentUser?.PortalRoles?.forEach((portalRole: PortalRoles) => {
      if (portalRole.RoleName === 'ONEOK Admin') {
        hasRole = true;
      }
    });
    return hasRole;
  }

  hasPortalEmployeeRole(): boolean {
    this.refreshCurrentContact();
    let hasRole = false;
    if (this.hasPortalAdminRole()) {
      hasRole = true;
    } else {
      this.currentUser?.PortalRoles?.forEach((portalRole: PortalRoles) => {
        if (portalRole.RoleName === 'ONEOK Employee') {
          hasRole = true;
        }
      });
    }

    return hasRole;
  }

  hasMeasurementEmployeeRole(): boolean {
    this.refreshCurrentContact();
    let hasRole = false;
    if (this.hasPortalAdminRole()) {
      hasRole = true;
    } else {
      this.currentUser?.PortalRoles?.forEach((portalRole: PortalRoles) => {
        if (portalRole.RoleName === 'ONEOK Employee - Measurement') {
          hasRole = true;
        }
      });
    }
    return hasRole;
  }

  hasOutagesEmployeeRole(): boolean {
    this.refreshCurrentContact();
    let hasRole = false;
    if (this.hasPortalAdminRole()) {
      hasRole = true;
    } else {
      this.currentUser?.PortalRoles?.forEach((portalRole: PortalRoles) => {
        if (portalRole.RoleName === 'ONEOK Employee - Outages') {
          hasRole = true;
        }
      });
    }
    return hasRole;
  }

  hasOutagesRole(): boolean {
    this.refreshCurrentContact();
    return !!(this.currentUser?.Modules as ContactModule[] | undefined)?.find(element => element.Name === 'Outages');
  }

  hasInvoicesRole(): boolean {
    this.refreshCurrentContact();
    return !!(this.currentUser?.Modules as ContactModule[] | undefined)?.find(element => element.Name === 'Invoices');
  }

  hasAccountAccessRole(): boolean {
    this.refreshCurrentContact();
    if (!this.currentUser) {
      return false;
    }
    let hasRole = false;
    if (this.currentUser.IsEmployee) {
      hasRole = true;
    } else {
      this.currentUser?.PortalRoles?.forEach((portalRole: PortalRoles) => {
        if (portalRole.RoleName === 'Account Access') {
          hasRole = true;
        }
      });
    }
    return hasRole;
  }

  hasMultipleAccountAccessRoles(): boolean {
    this.refreshCurrentContact();
    if (!this.currentUser) {
      return false;
    }
    let hasRoles = false;
    let acctCount = 0;

    if (this.currentUser.IsEmployee) {
      hasRoles = true;
    } else {
      this.currentUser?.PortalRoles?.forEach((portalRole: PortalRoles) => {
        if (portalRole.RoleName === 'Account Access') {
          acctCount++;
        }
      });
      if (acctCount > 1) {
        hasRoles = true;
      }
    }
    return hasRoles;
  }

  hasNGPBusinessSegment(): boolean {
    let hasSegment = false;
    this.refreshCurrentContact();

    if (this.hasPortalAdminRole()) {
      hasSegment = true;
    } else {
      (this.currentUser?.BusinessSegments as string[] | undefined)?.forEach((segment: string) => {
        if (segment === 'NGPIntrastate') {
          hasSegment = true;
        }
      });
    }

    return hasSegment;
  }

  hasNGGPBusinessSegment(): boolean {
    let hasSegment = false;
    this.refreshCurrentContact();
    if (this.hasPortalAdminRole() || this.hasMeasurementEmployeeRole() || this.hasOutagesEmployeeRole()) {
      hasSegment = true;
    } else {
      (this.currentUser?.BusinessSegments as string[] | undefined)?.forEach((segment: string) => {
        if (segment === 'NGGP') {
          hasSegment = true;
        }
      });
    }

    return hasSegment;
  }

  refreshCurrentContact(): void {
    this._storageSvc.getCurrentUser().subscribe(user => {
      this.currentUser = user?.Contact ?? null;
      this.currentUser?.PortalRoles?.forEach((portalRole: PortalRoles) => {
        if (portalRole.RoleName === 'ONEOK Admin') {
          this.isONEOKAdmin = true;
        }
      });
    });
  }

  hasAccountRole(roleName: string): void | boolean {
    if (this.currentAccount) {
      const role = this.currentAccount?.PortalRoles?.find(
        (element: PortalAccountRole) => element.RoleName === roleName
      );
      return role ? true : false;
    }
  }
}
